import { SVCheckbox, SVForm } from '@/components/core';
import { Card, Grid, Stack, Typography } from '@mui/material';
import { CompanyFormFields } from '@/types/companies';
import { useGetBrandingThemesList } from '@/hooks/requests/useBrandingThemes';
import { useGetStaffListForSelection } from '@/hooks/requests/useStaff';
import { useFetchProgramSettings } from '@/hooks/requests/useSettings';
import { ROUTES } from '@/constants/routes';
import FormSubmissionButtons from '@/components/FormSubmissionButtons';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { convertCompanyNameToSubdomain } from '@/utils/companies';
import BrandingThemeSection from './sections/BrandingThemeSection';
import BasicInfoSection from './sections/BasicInfoSection';
import SavingsInterestSection from './sections/SavingsInterestSection';
import InitialDepositSection from './sections/InitialDepositSection';
import FeeSection from './sections/FeeSection';
import PermissionsSection from './sections/PermissionSection';
import { useForm } from 'react-hook-form';
import { useAddCompany, useEditCompany } from '@/hooks/requests/useCompanies';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { initialCompanyForm } from '@/constants/companies';

type CompanyFormProps = {
  isEdit?: boolean;
  defaultValues?: CompanyFormFields;
  onLeave?: () => void;
  onSuccess: (uuid: string) => void;
};

export default function CompanyForm({
  isEdit,
  defaultValues,
  onLeave,
  onSuccess,
}: CompanyFormProps) {
  const navigate = useNavigate();
  const { addCompany, isAddCompanyPending } = useAddCompany();
  const { editCompany, isEditCompanyPending } = useEditCompany();
  const isPending = isAddCompanyPending || isEditCompanyPending;

  const formMethods = useForm<CompanyFormFields>({
    defaultValues: defaultValues ? defaultValues : initialCompanyForm,
    mode: 'onBlur',
  });
  const [selectedBrandingTheme, companyName] = formMethods.watch([
    'brandingThemeId',
    'name',
  ]);

  const { brandingThemes, isBrandingThemesPending } =
    useGetBrandingThemesList();
  const { staffList, isStaffListPending } = useGetStaffListForSelection();
  const { data: programSettings } = useFetchProgramSettings();

  const handleViewBrandingTheme = () => {
    if (selectedBrandingTheme) {
      navigate(`${ROUTES.BRANDING_THEME_VIEW_PARAMS}/${selectedBrandingTheme}`);
    }
  };

  const brandingThemeOptions =
    brandingThemes?.map(theme => ({
      label: theme.name,
      value: theme.uuid,
    })) || [];

  const staffOptions =
    staffList?.map(staff => ({
      label: staff.label,
      value: staff.value,
    })) || [];

  const generatedSubdomain = useMemo(() => {
    if (!companyName || companyName.trim() === '') {
      return '';
    }
    return convertCompanyNameToSubdomain(companyName);
  }, [companyName]);

  async function handleSubmit(data: CompanyFormFields) {
    try {
      let response = '';
      if (isEdit) {
        response = await editCompany(data);
      } else {
        response = await addCompany(data);
      }

      showToast({
        type: 'success',
        data: {
          content: `Company has been successfully ${isEdit ? 'edited' : 'added'}`,
        },
      });

      onSuccess(response);
    } catch (error: any) {
      showToast({
        type: 'error',
        data: {
          content: error?.response?.data
            ? error.response.data
            : MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <SVForm<CompanyFormFields> methods={formMethods} onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <BasicInfoSection
          staffOptions={staffOptions}
          isStaffListPending={isStaffListPending}
          generatedSubdomain={generatedSubdomain}
        />
        <SavingsInterestSection />
        <InitialDepositSection />
        <Card>
          <Typography variant="h5" mb={2}>
            Lending
          </Typography>
          <Grid size={{ xs: 12, md: 6 }}>
            <SVCheckbox name="lendingEnabled" label="Lending Enabled" />
          </Grid>
        </Card>
        <FeeSection programSettings={programSettings} />
        <PermissionsSection />
        <BrandingThemeSection
          brandingThemeOptions={brandingThemeOptions}
          isBrandingThemesPending={isBrandingThemesPending}
          selectedBrandingTheme={selectedBrandingTheme}
          onViewBrandingTheme={handleViewBrandingTheme}
        />
        <FormSubmissionButtons onLeave={onLeave} isLoading={isPending} />
      </Stack>
    </SVForm>
  );
}
